import type { FicheColor } from '@pharmafiche/schema';
import { useEffect, useRef } from 'react';

interface CardContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRecolor: (color: FicheColor) => void;
  colors: { color: FicheColor; hex: string }[];
}

export function CardContextMenu({
  x,
  y,
  onClose,
  onDuplicate,
  onDelete,
  onRecolor,
  colors,
}: CardContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      aria-label="Actions de la carte"
      className="fixed bg-white border-2 border-ink rounded-control shadow-ink font-hand text-[14px] py-1 z-50 min-w-[180px]"
      style={{ left: x, top: y }}
      data-testid="card-context-menu"
    >
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onDuplicate();
          onClose();
        }}
        className="w-full text-left px-3 py-1.5 hover:bg-accent-blue/10 flex items-center gap-2"
      >
        <span aria-hidden>📋</span> Dupliquer
      </button>
      <div className="px-3 py-1.5 border-t border-ink/20">
        <div className="text-ink-soft text-[12px] mb-1">Couleur</div>
        <div className="flex gap-2">
          {colors.map((c) => (
            <button
              key={c.color}
              type="button"
              role="menuitem"
              aria-label={`Recolorer en ${c.color}`}
              onClick={() => {
                onRecolor(c.color);
                onClose();
              }}
              className="w-6 h-6 rounded-full border-2 border-ink hover:scale-110 transition-transform"
              style={{ background: c.hex }}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onDelete();
          onClose();
        }}
        className="w-full text-left px-3 py-1.5 hover:bg-warn-bg text-warn-text border-t border-ink/20 flex items-center gap-2"
      >
        <span aria-hidden>🗑️</span> Supprimer
      </button>
    </div>
  );
}
